import React from 'react';
import { ArrowLeftRight, Camera, ExternalLink, Users } from 'lucide-react';
import hostPanelPNG from '@/assets/host-panel.png';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import EventDateTimePicker from '@/panel/components/EventDateTimePicker';
import { useEventDetails, STATUS_OPTIONS } from './EventDetailsContext';

function formatCount(value: number | null) {
  return value === null ? '—' : value.toLocaleString('es-AR');
}

const GeneralTab: React.FC = () => {
  const SHOW_LEGACY_STATS = false;
  const {
    event,
    albumUrl,
    status,
    handleStatusChange,
    eventDate,
    draftEventDate,
    setDraftEventDate,
    handleDateSave,
    photoCount,
    userCount,
    clientUser,
    clientDisplayName,
    clientEmailDisplay,
    clientAvatarInitial,
    openAssignModal,
    notesDraft,
    setNotesDraft,
    notesDirty,
    notesSaving,
    handleNotesSave,
    handleNotesCancel,
  } = useEventDetails();

  if (!event) return null;

  const hostPanelUrl = albumUrl ? `${albumUrl}/admin` : null;

  return (
    <div className="panel-card panel-card--main p-6 space-y-6">
      <div className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,0.7fr)_minmax(0,1.2fr)_minmax(0,1.2fr)]">
          <div className="rounded-xl border border-indigo-100 bg-white/90 p-4 shadow-sm">
            <div className="text-xs uppercase tracking-wide text-slate-400">Estado</div>
            <Select value={status} onValueChange={(value) => { void handleStatusChange(value as typeof status); }}>
              <SelectTrigger className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white text-sm font-medium text-slate-700">
                <SelectValue placeholder="Seleccionar estado" />
              </SelectTrigger>
              <SelectContent className="rounded-2xl">
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="capitalize">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-xl border border-indigo-100 bg-white/90 p-4 shadow-sm">
            <div className="text-xs uppercase tracking-wide text-slate-400">Fecha</div>
            <div className="mt-2 flex items-center gap-3">
              <EventDateTimePicker
                value={eventDate}
                draftValue={draftEventDate}
                onDraftChange={setDraftEventDate}
                onSave={() => { void handleDateSave(); }}
                align="start"
              />
            </div>
          </div>

          <div className="rounded-xl border border-indigo-100 bg-white/90 p-4 shadow-sm">
            <div className="mb-3 text-xs uppercase tracking-wide text-slate-400">Cliente</div>
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={clientUser?.avatar_url || ''} />
                <AvatarFallback>{clientAvatarInitial}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-slate-800">{clientDisplayName}</div>
                {clientEmailDisplay && <div className="truncate text-xs text-slate-400">{clientEmailDisplay}</div>}
              </div>
              <button
                type="button"
                onClick={() => { void openAssignModal(); }}
                className="h-10 w-10 shrink-0 grid place-items-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50"
                aria-label="Cambiar cliente"
              >
                <ArrowLeftRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-[28px] border border-indigo-100 bg-white/85 p-4 shadow-sm sm:col-span-2">
            <div className="grid gap-5 xl:grid-cols-[160px_minmax(0,1fr)_220px] xl:items-center">
              <div className="flex items-center justify-center xl:justify-start">
                <img
                  src={hostPanelPNG}
                  alt="Vista previa del Panel de anfitrión"
                  className="h-32 w-auto max-w-full object-contain xl:h-36"
                />
              </div>

              <div className="min-w-0 space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Panel de Anfitrión</h3>
                  <p className="mt-1 text-sm text-slate-500">Herramientas para gestionar en el día del evento</p>
                </div>

                <div className="space-y-2 text-sm text-slate-600">
                  <div>• Gestión de usuarios</div>
                  <div>• Administración y moderación de fotos</div>
                  <div>• Contenido para pantallas: Ranking y Galería en Vivo</div>
                </div>
              </div>

              <div className="flex xl:justify-end">
                <Button
                  asChild={!!hostPanelUrl}
                  disabled={!hostPanelUrl}
                  className="h-12 w-full rounded-2xl bg-violet-600 px-5 text-sm font-semibold text-white shadow-[0_16px_30px_rgba(124,77,255,0.24)] hover:bg-violet-700 xl:w-auto"
                >
                  {hostPanelUrl ? (
                    <a href={hostPanelUrl} target="_blank" rel="noopener noreferrer">
                      <span className="inline-flex items-center gap-2">
                        Abrir Panel
                        <ExternalLink className="h-4 w-4" />
                      </span>
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      Abrir Panel
                      <ExternalLink className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {SHOW_LEGACY_STATS ? (
            <>
              <div
                className="rounded-2xl border border-slate-200 bg-white/80 px-5 py-4 shadow-sm transition hover:bg-slate-50 cursor-pointer"
                onClick={() => window.open(`/${event.slug}/admin/fotos`, '_blank')}
              >
                <div className="flex items-center justify-between text-slate-600">
                  <Camera className="h-5 w-5" />
                  <span className="text-2xl font-semibold text-slate-800">{formatCount(photoCount)}</span>
                </div>
                <div className="mt-2 text-xs font-medium uppercase tracking-wide text-slate-400">Fotos</div>
              </div>

              <div
                className="rounded-2xl border border-slate-200 bg-white/80 px-5 py-4 shadow-sm transition hover:bg-slate-50 cursor-pointer"
                onClick={() => window.open(`/${event.slug}/admin/usuarios`, '_blank')}
              >
                <div className="flex items-center justify-between text-slate-600">
                  <Users className="h-5 w-5" />
                  <span className="text-2xl font-semibold text-slate-800">{formatCount(userCount)}</span>
                </div>
                <div className="mt-2 text-xs font-medium uppercase tracking-wide text-slate-400">Usuarios</div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Notas internas</h2>
          <p className="text-sm text-slate-500">Recordatorios sobre el evento, cliente, pagos, etc...</p>
        </div>
        <textarea
          value={notesDraft}
          onChange={(event) => setNotesDraft(event.target.value)}
          placeholder="Agregá tus notas internas acá..."
          className="w-full min-h-[240px] resize-vertical rounded-3xl border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-700 shadow-inner transition-all focus:outline-none focus:ring-2 focus:ring-[#7C4DFF]/70 focus:ring-offset-2 focus:ring-offset-white focus:shadow-[0_0_0_6px_rgba(124,77,255,0.16)]"
        />
        {notesDirty && (
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleNotesCancel}
              disabled={notesSaving}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => { void handleNotesSave(); }}
              disabled={notesSaving}
            >
              {notesSaving ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralTab;
